import React, { useCallback, useEffect, useMemo, useState } from 'react';
import classNames from 'classnames';
import SvgIcon from '@home/components/SvgIcon';
import { DropdownBox, List, Popover } from 'tea-component';
import { useIntl } from 'umi';
import { ChartType, type TChartType } from '@dbl/core/chart';
import { ChartTypeInfos } from '@dbl/core/chartEditor';
import { CHART_SWITCH_DISPLAY_ORDER, CHART_TYPE_ICON } from '@dbl/core/chartEditor';
import { LANGUAGE_EN } from '@home/_config/config';
import TableTragCard from './TableTragCard';
import ChartTragCard from './ChartTragCard';

type TChartSwitchMenuItemProps = {
  label: string;
  orderedTypes: TChartType[];
  activeChartType: TChartType;
  open: boolean;
  setOpen: (open: boolean) => void;
  onSwitchType: (type: TChartType) => void;
  getTypeLabel: (type: TChartType) => string;
};

const ChartSwitchMenuItem: React.FC<TChartSwitchMenuItemProps> = ({
  label,
  orderedTypes,
  activeChartType,
  open,
  setOpen,
  onSwitchType,
  getTypeLabel,
}) => {
  // 三点菜单关闭时，该节点会被卸载；在卸载时重置二级菜单开关，
  // 避免下次再次打开三点菜单时二级菜单“自动出现”。
  useEffect(() => {
    return () => {
      setOpen(false);
    };
  }, [setOpen]);

  return (
    <Popover
      trigger="click"
      placement="right-start"
      visible={open}
      onVisibleChange={(visible: boolean) => setOpen(visible)}
      overlay={
        <DropdownBox
          className={classNames('dbl-card-drag-operation-dropdown', 'dbl-card-chart-switch-submenu')}
          preventMouseDown
          onClick={(e: any) => e?.stopPropagation?.()}
        >
          <List type="option">
            {orderedTypes.map(type => {
              const isActive = type === activeChartType;
              const iconType = CHART_TYPE_ICON[type] ?? '';
              return (
                <List.Item
                  key={type}
                  className={classNames('dbl-card-chart-switch-submenu-item', {
                    'dbl-card-chart-switch-submenu-item--active': isActive,
                  })}
                  onClick={() => {
                    onSwitchType(type);
                    setOpen(false);
                  }}
                >
                  {iconType ? <SvgIcon type={iconType} /> : null}
                  <span className="dbl-card-chart-switch-submenu-item__label">
                    {getTypeLabel(type)}
                  </span>
                </List.Item>
              );
            })}
          </List>
        </DropdownBox>
      }
    >
      <div className="dbl-card-chart-switch-menu-trigger">
        <SvgIcon type="icon-chart-switch" />
        <span className="dbl-card-chart-switch-menu-trigger__label">
          {label}
        </span>
        <SvgIcon type="arrow-right" className="dbl-card-chart-switch-menu-trigger__arrow" />
      </div>
    </Popover>
  );
};

type TChartSwitchConfig = {
  show: boolean;
  chartTypes: TChartType[];
};

interface IChartSwitchTragCardProps {
  deleteCard: (cardInfo: any) => void;
  handleOnSubmit?: (() => void) | null;
  dataMsg: any;
  filterValues: any;
  userFilter: any;
  hasChartPrivilege: boolean;
  system?: string;
  isEdit: boolean;
  tooltipMinTopPx?: number;
  chartExplore: boolean;
  chartsInfoLoading: boolean;
  chartType: TChartType;
  chartConfig: {
    chart_switch: TChartSwitchConfig;
  };
  /** 报表预览：栅格列宽，用于窄卡顶栏收纳 */
  layoutGridColSpan?: number;
}

const normalizeChartSwitch = (raw: any): TChartSwitchConfig | undefined => {
  if (!raw || typeof raw !== 'object') return undefined;
  const show = Boolean(raw?.show);
  const chartTypes = Array.isArray(raw?.chartTypes) ? raw?.chartTypes : [];
  return { show, chartTypes };
};

const createCacheKey = (taskId?: string): string =>
  `${taskId || 'chart_task'}_${Date.now()}`;

const ChartSwitchTragCard: React.FC<IChartSwitchTragCardProps> = ({
  deleteCard,
  handleOnSubmit,
  dataMsg,
  filterValues,
  userFilter,
  hasChartPrivilege,
  system = 'datalab',
  isEdit,
  chartExplore,
  chartsInfoLoading,
  chartType,
  chartConfig,
  tooltipMinTopPx,
  layoutGridColSpan,
}) => {
  const { formatMessage, locale } = useIntl();
  const isEn = locale === LANGUAGE_EN;
  const [activeChartType, setActiveChartType] = useState<TChartType>(chartType);
  const [switchMenuOpen, setSwitchMenuOpen] = useState(false);
  const [switchRequestId, setSwitchRequestId] = useState(1);
  const [switchFilterValues, setSwitchFilterValues] = useState<any[] | null>(null);
  const [tableDownloadParams, setTableDownloadParams] = useState<Record<string, any>>({});
  const [cacheKey, setCacheKey] = useState<string>(() =>
    createCacheKey(String(dataMsg?.task_id || ''))
  );
  const [fetchedTypeMap, setFetchedTypeMap] = useState<Record<string, boolean>>({});
  const chartSwitchConfig = useMemo(
    () => normalizeChartSwitch(chartConfig?.chart_switch),
    [chartConfig?.chart_switch]
  );

  useEffect(() => {
    setActiveChartType(chartType);
  }, [chartType]);

  useEffect(() => {
    // 外部筛选器变化时，重置内部VS筛选覆盖
    setSwitchFilterValues(null);
  }, [filterValues]);

  useEffect(() => {
    if (chartsInfoLoading) return;
    if (!filterValues && !userFilter?.controls?.length) return;
    // 首次加载/筛选条件变化：重建 cache_key，并清空已请求类型缓存
    setCacheKey(createCacheKey(String(dataMsg?.task_id || '')));
    setFetchedTypeMap({});
    setSwitchRequestId((prev) => prev + 1);
  }, [dataMsg?.task_id, filterValues, userFilter, chartsInfoLoading]);

  const orderedTypes = useMemo(() => {
    if (!chartSwitchConfig) return [];
    const types = chartSwitchConfig?.chartTypes || [];
    const baseOrder = CHART_SWITCH_DISPLAY_ORDER;
    const inOrder = baseOrder.filter((t) => types.includes(t));
    const rest = types.filter((t) => !baseOrder.includes(t as TChartType));
    return [...inOrder, ...rest] as TChartType[];
  }, [chartSwitchConfig]);

  const showSwitch = Boolean(chartSwitchConfig?.show) && orderedTypes.length > 1;

  const handleSwitchType = useCallback((type: TChartType) => {
    if (type === activeChartType) return;
    setActiveChartType(type);
    // 纯图表切换：复用同一个 cache_key；若该类型已请求过则直接复用缓存，不再请求接口
    if (!fetchedTypeMap?.[type]) {
      setSwitchRequestId((prev) => prev + 1);
    }
  }, [activeChartType, fetchedTypeMap]);

  const handleTypeDataLoaded = useCallback((type: TChartType) => {
    setFetchedTypeMap((prev) => {
      if (prev?.[type]) return prev;
      return {
        ...prev,
        [type]: true,
      };
    });
  }, []);

  const handleCompareTimeFiltersChange = useCallback((nextFilters: any[]) => {
    const normalizedFilters = Array.isArray(nextFilters)
      ? nextFilters.reduce<any[]>((acc, item) => {
        const key = String(item?.filterKey || item?.table_id || '');
        if (!key) {
          acc.push({ ...(item || {}) });
          return acc;
        }
        const existedIndex = acc.findIndex((opt) => {
          const optKey = String(opt?.filterKey || opt?.table_id || '');
          return optKey === key;
        });
        if (existedIndex > -1) {
          acc[existedIndex] = { ...(item || {}) };
        } else {
          acc.push({ ...(item || {}) });
        }
        return acc;
      }, [])
      : [];
    setSwitchFilterValues(normalizedFilters);
    // VS设置影响SQL：生成新cache_key，并触发当前图表重算；其他类型缓存失效
    setCacheKey(createCacheKey(String(dataMsg?.task_id || '')));
    setFetchedTypeMap({});
    setSwitchRequestId((prev) => prev + 1);
  }, [dataMsg?.task_id]);

  const chartSwitchNode = useMemo(() => {
    if (!showSwitch) return null;

    return (
      <ChartSwitchMenuItem
        label={formatMessage({ id: 'dbl_chart_switch' })}
        orderedTypes={orderedTypes}
        activeChartType={activeChartType}
        open={switchMenuOpen}
        setOpen={setSwitchMenuOpen}
        onSwitchType={handleSwitchType}
        getTypeLabel={type => {
          const info = ChartTypeInfos?.[type] || {};
          return (isEn ? info?.nameEn : info?.nameCn) || String(type);
        }}
      />
    );
  }, [
    activeChartType,
    isEn,
    formatMessage,
    handleSwitchType,
    orderedTypes,
    showSwitch,
    switchMenuOpen,
  ]);

  const typesToMount = useMemo(() => {
    const baseTypes: TChartType[] = [ChartType.Table, ChartType.Bar, ChartType.Line];
    const extraTypes = (chartSwitchConfig?.chartTypes || []) as TChartType[];
    const set = new Set<TChartType>([...baseTypes, ...extraTypes, activeChartType]);
    return [...set];
  }, [chartSwitchConfig, activeChartType]);

  const renderCardForType = useCallback((type: TChartType) => {
    const isActive = type === activeChartType;
    const commonClassName = classNames('dbl-chart-switch-trag-card-content', { 'dbl-chart-switch-trag-card-content-active': isActive });
    const commonProps = {
      deleteCard,
      handleOnSubmit,
      dataMsg,
      filterValues: switchFilterValues || filterValues,
      userFilter,
      hasChartPrivilege,
      system,
      isEdit,
      chartExplore,
      chartsInfoLoading,
      isActive,
      chartSwitchNode,
      cacheKey,
      switchRequestId,
      shouldRequest: !fetchedTypeMap?.[type],
      onTypeDataLoaded: handleTypeDataLoaded,
      onCompareTimeFiltersChange: handleCompareTimeFiltersChange,
      onDownloadParamsChange: setTableDownloadParams,
      tableDownloadParams,
      tooltipMinTopPx,
      layoutGridColSpan,
    };

    if (type === ChartType.Table) {
      return (
        <div key={type} className={commonClassName}>
          <TableTragCard
            {...commonProps}
          />
        </div>
      );
    }

    return (
      <div key={type} className={commonClassName}>
        <ChartTragCard
          {...commonProps}
          chartType={type}
        />
      </div>
    );
  }, [
    activeChartType,
    chartsInfoLoading,
    chartSwitchNode,
    chartExplore,
    cacheKey,
    dataMsg,
    deleteCard,
    fetchedTypeMap,
    filterValues,
    switchFilterValues,
    handleCompareTimeFiltersChange,
    handleTypeDataLoaded,
    handleOnSubmit,
    hasChartPrivilege,
    isEdit,
    tooltipMinTopPx,
    switchRequestId,
    system,
    tableDownloadParams,
    userFilter,
    layoutGridColSpan,
  ]);

  return (
    <div className="dbl-chart-switch-trag-card">
      {/* 仅隐藏：不卸载各类卡片，尽量复用组件内部状态 */}
      {typesToMount?.map((type: TChartType) => renderCardForType(type))}
    </div>
  );
};

export default ChartSwitchTragCard;


